import { usePostHog } from 'posthog-js/react';
import {
    Component,
    type ErrorInfo,
    Fragment,
    type ReactNode,
    useEffect,
} from 'react';
import { z } from 'zod';

import {
    dataCaregiverListAdapter,
    dataClientDetailAdapter,
    dataShiftListAdapter,
} from '@apps/web/components/phoebe_chat_view/dynamic_widgets/bash_presentation_adapters';
import { caregiverMatchesAdapter } from '@apps/web/components/phoebe_chat_view/dynamic_widgets/caregiver_matches_adapter';
import {
    type DynamicWidgetActionRequest,
    type DynamicWidgetActionStateResolver,
} from '@apps/web/components/phoebe_chat_view/dynamic_widgets/dynamic_widget_actions';
import { dynamicWidgetInspectorStore } from '@apps/web/components/phoebe_chat_view/dynamic_widgets/dynamic_widget_inspector_store';
import { DynamicWidgetIssueNotice } from '@apps/web/components/phoebe_chat_view/dynamic_widgets/dynamic_widget_issue_notice';
import {
    captureDynamicWidgetMetric,
    DynamicWidgetMetricReporter,
    getDynamicWidgetMetricTags,
    type PostHogCaptureClient,
} from '@apps/web/components/phoebe_chat_view/dynamic_widgets/dynamic_widget_metrics';
import {
    type CaregiverListWidgetModel,
    type ClientDetailWidgetModel,
    type DynamicWidgetModel,
    type GenericTableWidgetModel,
    getDynamicWidgetSourceId,
    type OutreachHistoryWidgetModel,
    type OutreachProgressWidgetModel,
    type ResourceShiftListWidgetModel,
    type ShiftListWidgetModelV1,
} from '@apps/web/components/phoebe_chat_view/dynamic_widgets/dynamic_widget_schema';
import {
    DynamicWidgetModelContext,
    useDynamicWidgetSupportActions,
} from '@apps/web/components/phoebe_chat_view/dynamic_widgets/dynamic_widget_support_context';
import { genericTableAdapter } from '@apps/web/components/phoebe_chat_view/dynamic_widgets/generic_table_adapter';
import { outreachProgressAdapter } from '@apps/web/components/phoebe_chat_view/dynamic_widgets/outreach_progress_adapter';
import { retrieveCaregiverListAdapter } from '@apps/web/components/phoebe_chat_view/dynamic_widgets/retrieve_caregiver_list_adapter';
import { retrieveClientDetailAdapter } from '@apps/web/components/phoebe_chat_view/dynamic_widgets/retrieve_client_detail_adapter';
import { retrieveOutreachHistoryAdapter } from '@apps/web/components/phoebe_chat_view/dynamic_widgets/retrieve_outreach_history_adapter';
import { retrieveShiftListAdapter } from '@apps/web/components/phoebe_chat_view/dynamic_widgets/retrieve_shift_list_adapter';
import { shiftListAdapter } from '@apps/web/components/phoebe_chat_view/dynamic_widgets/shift_list_adapter';
import { type ToolExecutionEntry } from '@apps/web/components/phoebe_chat_view/transcript_types';
import { EVENTS } from '@apps/web/lib/events';

export type DynamicWidgetBuildContext = {
    readonly organizationId: string;
    readonly mode: 'live' | 'sandbox';
    readonly organizationTimezone: string;
};

export type DynamicWidgetHandlers = {
    readonly onCaregiverClick?: (caregiverId: string) => void;
    readonly onClientClick?: (clientId: string) => void;
    readonly onPromptAction?: (
        prompt: string,
    ) => Promise<boolean | void> | boolean | void;
    readonly onWidgetAction?: (
        action: DynamicWidgetActionRequest,
    ) => Promise<boolean | void> | boolean | void;
    readonly getActionState?: DynamicWidgetActionStateResolver;
};

export type DynamicWidgetAdapter<TModel extends DynamicWidgetModel> = {
    readonly template: TModel['template'];
    readonly templateVersion: TModel['templateVersion'];
    readonly toolNames: ReadonlySet<string>;
    readonly matches?: (entry: ToolExecutionEntry) => boolean;
    readonly build: (
        entry: ToolExecutionEntry,
        context: DynamicWidgetBuildContext,
    ) => TModel | null;
    readonly render: (
        model: TModel,
        handlers: DynamicWidgetHandlers,
    ) => ReactNode;
};

/**
 * One registry entry, tied to the model member its template builds. This is
 * the type an adapter file annotates: `template` discriminates the union, so
 * an adapter's `build` and `render` see their own model type instead of every
 * template's.
 */
type RegisteredDynamicWidgetAdapter =
    | DynamicWidgetAdapter<ShiftListWidgetModelV1>
    | DynamicWidgetAdapter<ResourceShiftListWidgetModel>
    | DynamicWidgetAdapter<OutreachProgressWidgetModel>
    | DynamicWidgetAdapter<CaregiverListWidgetModel>
    | DynamicWidgetAdapter<OutreachHistoryWidgetModel>
    | DynamicWidgetAdapter<ClientDetailWidgetModel>
    | DynamicWidgetAdapter<GenericTableWidgetModel>;

const dynamicWidgetAdapters: ReadonlyArray<RegisteredDynamicWidgetAdapter> = [
    genericTableAdapter,
    dataShiftListAdapter,
    dataCaregiverListAdapter,
    dataClientDetailAdapter,
    retrieveShiftListAdapter,
    retrieveCaregiverListAdapter,
    caregiverMatchesAdapter,
    retrieveOutreachHistoryAdapter,
    retrieveClientDetailAdapter,
    shiftListAdapter,
    outreachProgressAdapter,
];

export type DynamicWidgetCompositionOutcome =
    | 'rendered'
    | 'replaced_same_result'
    | 'supporting_result';

const compositionOutcomeByEntry = new WeakMap<
    ToolExecutionEntry,
    DynamicWidgetCompositionOutcome
>();
const compositionOutcomeByModel = new WeakMap<
    DynamicWidgetModel,
    DynamicWidgetCompositionOutcome
>();

export function recordDynamicWidgetCompositionOutcome(
    entry: ToolExecutionEntry,
    outcome: DynamicWidgetCompositionOutcome,
): void {
    compositionOutcomeByEntry.set(entry, outcome);
}

export function getDynamicWidgetCompositionOutcome(
    entry: ToolExecutionEntry,
): DynamicWidgetCompositionOutcome {
    return compositionOutcomeByEntry.get(entry) ?? 'rendered';
}

export function isDynamicWidgetEligibleEntry(
    entry: ToolExecutionEntry,
): boolean {
    return (
        entry.isComplete &&
        getEffectiveDynamicWidgetLifecycleStatus(entry) === 'completed' &&
        !entry.error &&
        typeof entry.toolCallEventId === 'string' &&
        dynamicWidgetAdapters.some((adapter) => adapterMatches(adapter, entry))
    );
}

function getEffectiveDynamicWidgetLifecycleStatus(
    entry: ToolExecutionEntry,
): 'running' | 'completed' | 'failed' {
    if (entry.lifecycleStatus !== undefined) {
        return entry.lifecycleStatus === 'announced'
            ? 'running'
            : entry.lifecycleStatus;
    }
    if (!entry.isComplete) {
        return 'running';
    }
    const normalizedStatus = entry.status?.trim().toLowerCase() ?? '';
    return entry.error ||
        normalizedStatus === 'failed' ||
        normalizedStatus === 'failure' ||
        normalizedStatus === 'incomplete' ||
        normalizedStatus === 'cancelled' ||
        normalizedStatus === 'error'
        ? 'failed'
        : 'completed';
}

function adapterMatches(
    adapter: RegisteredDynamicWidgetAdapter,
    entry: ToolExecutionEntry,
): boolean {
    return adapter.matches?.(entry) ?? adapter.toolNames.has(entry.name);
}

function getMatchingAdapters(
    entry: ToolExecutionEntry,
): ReadonlyArray<RegisteredDynamicWidgetAdapter> {
    return dynamicWidgetAdapters
        .filter((adapter) => adapterMatches(adapter, entry))
        .sort((left, right) => right.templateVersion - left.templateVersion);
}

function normalizePresentationKeyValue(value: unknown): unknown {
    if (Array.isArray(value)) {
        return value.map(normalizePresentationKeyValue);
    }
    if (typeof value !== 'object' || value === null) {
        return value ?? null;
    }
    return Object.fromEntries(
        Object.entries(value as Record<string, unknown>)
            .filter(([key]) => key !== 'presentation_intent')
            .sort(([left], [right]) => left.localeCompare(right))
            .map(([key, nestedValue]) => [
                key,
                normalizePresentationKeyValue(nestedValue),
            ]),
    );
}

/**
 * Identifies one logical result inside a user turn. The value stays in memory
 * only; it is never emitted as an analytics property because arguments can
 * contain customer search text.
 */
export function getDynamicWidgetPresentationKey(
    entry: ToolExecutionEntry,
): string | null {
    const adapter = getMatchingAdapters(entry)[0];
    if (adapter === undefined) {
        return null;
    }
    return JSON.stringify({
        template: adapter.template,
        toolName: entry.name,
        arguments: normalizePresentationKeyValue(entry.arguments),
    });
}

function buildDynamicWidgetModel(
    entry: ToolExecutionEntry,
    context: DynamicWidgetBuildContext,
    reportValidationFailure: boolean,
): DynamicWidgetModel | null {
    if (!isDynamicWidgetEligibleEntry(entry)) {
        return null;
    }

    const adapters = getMatchingAdapters(entry);
    let buildFailure:
        | {
              readonly adapter: RegisteredDynamicWidgetAdapter;
              readonly errorClass: string;
              readonly issueCount?: number;
          }
        | undefined;
    for (const adapter of adapters) {
        try {
            const model = adapter.build(entry, context);
            if (model !== null) {
                compositionOutcomeByModel.set(
                    model,
                    getDynamicWidgetCompositionOutcome(entry),
                );
                return model;
            }
        } catch (error) {
            buildFailure ??= {
                adapter,
                errorClass:
                    error instanceof Error && error.name.length > 0
                        ? error.name
                        : 'non_error_throw',
                ...(error instanceof z.ZodError
                    ? { issueCount: Math.min(error.issues.length, 20) }
                    : {}),
            };
        }
    }

    if (buildFailure && reportValidationFailure) {
        // Keep warnings bounded and free of raw tool output. Model-building
        // errors happen before the React error boundary, so they must degrade
        // to the classic renderer here instead of escaping the transcript.
        // oxlint-disable-next-line no-console
        console.warn('Dynamic widget validation failed.', {
            toolName: entry.name,
            template: buildFailure.adapter.template,
            templateVersion: buildFailure.adapter.templateVersion,
            ...(buildFailure.issueCount === undefined
                ? { errorClass: buildFailure.errorClass }
                : { issueCount: buildFailure.issueCount }),
        });
    }
    return null;
}

export function canBuildDynamicWidget(
    entry: ToolExecutionEntry,
    context: DynamicWidgetBuildContext,
): boolean {
    return buildDynamicWidgetModel(entry, context, false) !== null;
}

export function buildDynamicWidget(
    entry: ToolExecutionEntry,
    context: DynamicWidgetBuildContext,
): DynamicWidgetModel | null {
    return buildDynamicWidgetModel(entry, context, true);
}

function renderRegisteredDynamicWidget(
    model: DynamicWidgetModel,
    handlers: DynamicWidgetHandlers,
): ReactNode | null {
    switch (model.template) {
        case 'table':
            return genericTableAdapter.render(model, handlers);
        case 'shifts':
            switch (model.templateVersion) {
                case 1:
                    return shiftListAdapter.render(model, handlers);
                case 3:
                    return retrieveShiftListAdapter.render(model, handlers);
                default: {
                    const exhaustiveVersion: never = model;
                    return exhaustiveVersion;
                }
            }
        case 'progress':
            return outreachProgressAdapter.render(model, handlers);
        case 'entities':
            // Ranked matches hydrate from the recommendation artifact; the
            // directory listing hydrates from the retrieve artifact.
            return model.variant === 'ranked'
                ? caregiverMatchesAdapter.render(model, handlers)
                : retrieveCaregiverListAdapter.render(model, handlers);
        case 'outreach_history':
            return retrieveOutreachHistoryAdapter.render(model, handlers);
        case 'detail':
            return retrieveClientDetailAdapter.render(model, handlers);
        default: {
            const exhaustiveModel: never = model;
            return exhaustiveModel;
        }
    }
}

function RegisteredDynamicWidgetRenderer({
    model,
    handlers,
}: {
    readonly model: DynamicWidgetModel;
    readonly handlers: DynamicWidgetHandlers;
}) {
    const content = renderRegisteredDynamicWidget(model, handlers);
    const tags = getDynamicWidgetMetricTags(model);
    const compositionOutcome =
        compositionOutcomeByModel.get(model) ?? 'rendered';
    const firstUsefulDurationMs = getFirstUsefulWidgetDurationMs(model);
    return (
        <Fragment>
            <DynamicWidgetMetricReporter
                event={EVENTS.DYNAMIC_WIDGET_PRESENTATION}
                tags={{ ...tags, outcome: 'candidate' }}
                dedupeKey={`${model.widgetId}:candidate`}
            />
            <DynamicWidgetMetricReporter
                event={EVENTS.DYNAMIC_WIDGET_PRESENTATION}
                tags={{ ...tags, outcome: compositionOutcome }}
                dedupeKey={`${model.widgetId}:${compositionOutcome}`}
            />
            <DynamicWidgetMetricReporter
                event={EVENTS.DYNAMIC_WIDGET_BUILT}
                tags={tags}
                dedupeKey={model.widgetId}
            />
            {firstUsefulDurationMs === null ? null : (
                <DynamicWidgetMetricReporter
                    event={EVENTS.DYNAMIC_WIDGET_FIRST_USEFUL}
                    tags={{
                        ...tags,
                        outcome: compositionOutcome,
                        durationMs: firstUsefulDurationMs,
                    }}
                    dedupeKey={`${model.widgetId}:first-useful`}
                />
            )}
            {content}
        </Fragment>
    );
}

function getFirstUsefulWidgetDurationMs(
    model: DynamicWidgetModel,
): number | null {
    const capturedTimes = model.sources.flatMap((source) => {
        if (source.kind !== 'event_source') {
            return [];
        }
        const capturedAt = Date.parse(source.capturedAt);
        return Number.isFinite(capturedAt) ? [capturedAt] : [];
    });
    if (capturedTimes.length === 0) {
        return null;
    }
    const capturedAt = Math.max(...capturedTimes);
    const now = Date.now();
    const pageStartedAt = globalThis.performance?.timeOrigin ?? now;
    if (capturedAt < pageStartedAt || capturedAt > now) {
        return null;
    }
    return now - capturedAt;
}

type DynamicWidgetErrorBoundaryState = {
    readonly errorClass: string | null;
    readonly showClassicView: boolean;
};

type DynamicWidgetErrorBoundaryClassProps = {
    readonly model: DynamicWidgetModel;
    readonly classicFallback: ReactNode;
    readonly posthog: PostHogCaptureClient | null | undefined;
    readonly children: ReactNode;
};

class DynamicWidgetErrorBoundaryClass extends Component<
    DynamicWidgetErrorBoundaryClassProps,
    DynamicWidgetErrorBoundaryState
> {
    public override state: DynamicWidgetErrorBoundaryState = {
        errorClass: null,
        showClassicView: false,
    };

    public static getDerivedStateFromError(
        error: unknown,
    ): DynamicWidgetErrorBoundaryState {
        return {
            errorClass:
                error instanceof Error && error.name.length > 0
                    ? error.name
                    : 'non_error_throw',
            showClassicView: false,
        };
    }

    public override componentDidCatch(
        error: unknown,
        _errorInfo: ErrorInfo,
    ): void {
        const errorClass =
            error instanceof Error && error.name.length > 0
                ? error.name
                : 'non_error_throw';
        captureDynamicWidgetMetric(
            this.props.posthog,
            EVENTS.DYNAMIC_WIDGET_FALLBACK,
            {
                ...getDynamicWidgetMetricTags(this.props.model),
                errorCode: 'render_failed',
                errorClass,
            },
        );
        captureDynamicWidgetMetric(
            this.props.posthog,
            EVENTS.DYNAMIC_WIDGET_PRESENTATION,
            {
                ...getDynamicWidgetMetricTags(this.props.model),
                outcome: 'candidate',
            },
        );
        captureDynamicWidgetMetric(
            this.props.posthog,
            EVENTS.DYNAMIC_WIDGET_PRESENTATION,
            {
                ...getDynamicWidgetMetricTags(this.props.model),
                outcome: 'malformed_fallback',
                errorCode: 'render_failed',
                errorClass,
            },
        );
    }

    public override render(): ReactNode {
        if (this.state.showClassicView) {
            return (
                <div data-dynamic-widget-classic-view="true">
                    {this.props.classicFallback}
                </div>
            );
        }
        if (this.state.errorClass !== null) {
            const model = this.props.model;
            return (
                <DynamicWidgetIssueNotice
                    issue={{
                        id: `${model.widgetId}.render_failed`,
                        code: 'render_failed',
                        level: 'error',
                        scope: 'widget',
                        bindingIds: [],
                        sourceIds: model.sources.map(getDynamicWidgetSourceId),
                        retryable: false,
                        safeDetails: {
                            errorClass: this.state.errorClass,
                        },
                    }}
                    metricTags={getDynamicWidgetMetricTags(model)}
                    onUseClassicView={
                        this.props.classicFallback === null ||
                        this.props.classicFallback === undefined
                            ? undefined
                            : () => {
                                  captureDynamicWidgetMetric(
                                      this.props.posthog,
                                      EVENTS.DYNAMIC_WIDGET_PRESENTATION,
                                      {
                                          ...getDynamicWidgetMetricTags(
                                              this.props.model,
                                          ),
                                          outcome: 'classic_fallback',
                                          errorCode: 'render_failed',
                                          errorClass:
                                              this.state.errorClass ??
                                              'unknown_error',
                                      },
                                  );
                                  this.setState({
                                      errorClass: this.state.errorClass,
                                      showClassicView: true,
                                  });
                              }
                    }
                />
            );
        }
        return this.props.children;
    }
}

export function DynamicWidgetErrorBoundary({
    model,
    classicFallback,
    children,
}: {
    readonly model: DynamicWidgetModel;
    readonly classicFallback: ReactNode;
    readonly children: ReactNode;
}) {
    const posthog = usePostHog();
    return (
        <DynamicWidgetErrorBoundaryClass
            model={model}
            classicFallback={classicFallback}
            posthog={posthog}
        >
            {children}
        </DynamicWidgetErrorBoundaryClass>
    );
}

export function DynamicWidgetFallbackMetric({
    entry,
    errorClass = 'model_not_built',
}: {
    readonly entry: ToolExecutionEntry;
    readonly errorClass?: string;
}) {
    const adapter = getMatchingAdapters(entry)[0];
    if (
        adapter === undefined ||
        !entry.isComplete ||
        getEffectiveDynamicWidgetLifecycleStatus(entry) !== 'completed' ||
        entry.error
    ) {
        return null;
    }
    const tags = {
        template: adapter.template,
        templateVersion: adapter.templateVersion,
        toolName: entry.name,
        errorClass,
    } as const;
    const key = entry.toolCallEventId ?? entry.id;
    return (
        <Fragment>
            <DynamicWidgetMetricReporter
                event={EVENTS.DYNAMIC_WIDGET_PRESENTATION}
                tags={{ ...tags, outcome: 'candidate' }}
                dedupeKey={`${key}:candidate`}
            />
            <DynamicWidgetMetricReporter
                event={EVENTS.DYNAMIC_WIDGET_PRESENTATION}
                tags={{ ...tags, outcome: 'malformed_fallback' }}
                dedupeKey={`${key}:malformed-fallback`}
            />
            <DynamicWidgetMetricReporter
                event={EVENTS.DYNAMIC_WIDGET_FALLBACK}
                tags={tags}
                dedupeKey={`${key}:fallback`}
            />
        </Fragment>
    );
}

/**
 * Publishes the rendered model to the binding inspector, but only while the
 * internal-admin debug sidebar is open. Nothing is collected for anyone else.
 */
function DynamicWidgetInspectorRegistration({
    model,
}: {
    readonly model: DynamicWidgetModel;
}) {
    const isInspectionOpen =
        useDynamicWidgetSupportActions()?.isInspectionOpen === true;
    useEffect(() => {
        if (!isInspectionOpen) {
            return;
        }
        return dynamicWidgetInspectorStore.register(model);
    }, [isInspectionOpen, model]);
    return null;
}

export function renderDynamicWidget(
    model: DynamicWidgetModel,
    handlers: DynamicWidgetHandlers,
    classicFallback: ReactNode = null,
): ReactNode | null {
    return (
        <DynamicWidgetModelContext.Provider value={model}>
            <DynamicWidgetInspectorRegistration model={model} />
            <DynamicWidgetErrorBoundary
                model={model}
                classicFallback={classicFallback}
            >
                <RegisteredDynamicWidgetRenderer
                    model={model}
                    handlers={handlers}
                />
            </DynamicWidgetErrorBoundary>
        </DynamicWidgetModelContext.Provider>
    );
}
